import { MathUtils, Vector2, Vector3 } from 'three'

/**
 * Calculate the angle of a line on a given plane.
 *
 * @param start The starting point of the line.
 * @param end The ending point of the line.
 * @param planeNormal The normal vector of the plane.
 * @param up The up vector (usually the world up vector).
 * @returns The angle of the line on the plane in degrees, normalized to avoid flipping upside down.
 */
export const lineAngleOnPlane = (start: Vector3, end: Vector3, planeNormal: Vector3, up: Vector3): number => {
  const z = planeNormal.clone().normalize()

  // Ortho up onto plane → Y axis
  const y = up
    .clone()
    .sub(z.clone().multiplyScalar(up.dot(z)))
    .normalize()

  // X axis = Y × Z
  const x = new Vector3().crossVectors(y, z).normalize()

  // Project line into plane coordinates
  const dir = end.clone().sub(start).normalize()
  const px = dir.dot(x)
  const py = dir.dot(y)

  // Angle in plane (atan2 for proper quadrant)
  const angleRad = Math.atan2(py, px)
  let cssDeg = -MathUtils.radToDeg(angleRad)

  // Normalize so text never flips upside down
  if (cssDeg > 90) cssDeg += 180
  if (cssDeg < -90) cssDeg -= 180

  return cssDeg
}

/**
 * Generate points for an arrow for each end of a line segment.
 *
 * @param point1 The first point of the line.
 * @param point2 The second point of the line.
 * @param normal The normal vector of the plane.
 * @param arrowLength The length of the arrow (optional, defaults to 0.01).
 * @param arrowWidth The width of the arrow (optional, defaults to 0.0035).
 * @returns An array of Vector3 points representing the arrow.
 */
export const generateArrowPoints = (() => {
  const tempVector1 = new Vector3()
  const tempVector2 = new Vector3()
  const tempLeftVector = new Vector3(0, 1, 0)

  return (
    point1: Vector3,
    point2: Vector3,
    normal: Vector3,
    arrowLength: number = 0.01,
    arrowWidth: number = 0.0035,
  ): Vector3[] => {
    tempVector1.copy(point1)
    tempVector2.copy(point2)

    const direction = new Vector3().subVectors(tempVector1, tempVector2).normalize()
    tempLeftVector.copy(direction).cross(normal).normalize()

    return [
      tempVector1.clone(),
      tempVector1
        .clone()
        .add(direction.clone().multiplyScalar(-arrowLength))
        .add(tempLeftVector.clone().multiplyScalar(arrowWidth)),
      tempVector1
        .clone()
        .add(direction.clone().multiplyScalar(-arrowLength))
        .add(tempLeftVector.clone().multiplyScalar(-arrowWidth)),
    ].flat()
  }
})()

/**
 * Expands a 3D rectangle (lying flat on a plane) by side-specific amounts,
 * similar to CSS padding shorthand (array of 1–4 numbers).
 *
 * @param corners The 4 corners of the rectangle in 3D space (ordered).
 * @param normal The plane normal the rectangle lies on.
 * @param padding Shorthand array for padding:
 *   [all] | [vertical, horizontal] | [top, horizontal, bottom] | [top, right, bottom, left].
 * @returns Expanded rectangle as an array of objects `{ uv, world }`,
 *          where `uv` is the 2D plane-space coordinate and `world` is the 3D world-space coordinate,
 *          or null if invalid input.
 */
export const expandRectangle3D = (
  corners: Vector3[],
  normal: Vector3,
  padding: number[],
): { uv: Vector2; world: Vector3 }[] | null => {
  if (corners.length !== 4) return null

  // normalize padding shorthand
  let top: number
  let right: number
  let bottom: number
  let left: number

  if (padding.length === 1) {
    ;[top] = padding
    right = top
    bottom = top
    left = top
  } else if (padding.length === 2) {
    ;[top, right] = padding
    bottom = top
    left = right
  } else if (padding.length === 3) {
    ;[top, right, bottom] = padding
    left = right
  } else if (padding.length === 4) {
    ;[top, right, bottom, left] = padding
  } else {
    return null
  }

  // Basis vectors for the rectangle’s plane
  const origin = corners[0].clone()
  const xAxis = new Vector3().subVectors(corners[1], corners[0]).normalize()
  const yAxis = new Vector3().crossVectors(normal, xAxis).normalize()

  // Project corners into 2D plane space relative to origin
  const coords2D = corners.map((c) => new Vector2(c.clone().sub(origin).dot(xAxis), c.clone().sub(origin).dot(yAxis)))

  // Bounds in 2D
  let minX = Math.min(...coords2D.map((c) => c.x))
  let maxX = Math.max(...coords2D.map((c) => c.x))
  let minY = Math.min(...coords2D.map((c) => c.y))
  let maxY = Math.max(...coords2D.map((c) => c.y))

  // Apply side-specific padding (negative works too, like CSS)
  minX -= left
  maxX += right
  minY -= bottom
  maxY += top

  // Expanded rectangle in 2D
  const expanded2D = [
    new Vector2(minX, minY), // bottom-left
    new Vector2(maxX, minY), // bottom-right
    new Vector2(maxX, maxY), // top-right
    new Vector2(minX, maxY), // top-left
  ]

  // Return with paired uv/world format
  return expanded2D.map((uv) => ({
    uv,
    world: origin.clone().addScaledVector(xAxis, uv.x).addScaledVector(yAxis, uv.y),
  }))
}

/**
 * Safely converts a thickness value to a number, handling potential undefined/null values
 * @param thickness - The thickness value that might be undefined, null, or a number
 * @returns A safe number value, defaulting to 0 if invalid
 */
export const getSafeThickness = (thickness?: number | null): number => Number(thickness) || 0
